import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Loader2, X } from 'lucide-react';
import { toast } from 'react-toastify';
import { useSendSecurityEmailMutation } from '../../redux/apiSlice';

interface AlreadyLoggedInModalProps {
  onClose: () => void;
  email: string;
}

const AlreadyLoggedInModal: React.FC<AlreadyLoggedInModalProps> = ({
  onClose,
  email,
}) => {
  const navigate = useNavigate();
  const [sendSecurityEmail, { isLoading }] = useSendSecurityEmailMutation();

  const handleSendSecurityEmail = async () => {
    try {
      await sendSecurityEmail(email).unwrap();
      toast.success(
        'Security email sent successfully! Please check your email.',
      );
      onClose();
    } catch (error) {
      toast.error('Failed to send security email. Please try again.');
    }
  };

  return (
    <div className="z-50 fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="p-6">
          <div className="flex justify-between items-center mb-4">
            <h2 className="text-2xl font-bold text-red-600">
              Account Already Active
            </h2>
            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-500"
            >
              <X className="h-6 w-6" />
            </button>
          </div>

          <div className="space-y-4">
            <p className="text-gray-700">
              It looks like your account is already logged in on another device.
              If you believe someone else is using your credentials without
              permission, click the button below to send a security email to
              your registered address. This email will help you disconnect the
              current session and guide you on how to change your account email
              for extra safety. Please make sure to keep your login details
              secure and avoid sharing them.
            </p>
          </div>
        </div>

        <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse rounded-b-lg">
          <button
            type="button"
            onClick={() => handleSendSecurityEmail()}
            className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-indigo-600 text-base font-medium text-white hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:ml-3 sm:w-auto sm:text-sm"
            disabled={isLoading}
          >
            {isLoading ? (
              <Loader2 className="animate-spin h-6 w-6 m-auto" />
            ) : (
              'Send Security Email'
            )}
          </button>
          <button
            type="button"
            onClick={onClose}
            className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:w-auto sm:text-sm"
            disabled={isLoading}
          >
            Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default AlreadyLoggedInModal;
